import React from 'react';
import styled from 'styled-components';

const BLUE = '#2c5f8a';
const BLUE_LT = '#ebf2f8';
const BLUE_DK = '#1e4466';
const T1 = '#1a1d21';
const T2 = '#5c6370';
const T3 = '#8c9198';
const BD = 'rgba(0, 0, 0, 0.1)';
const GRAY_50 = '#f5f6f7';

const IntroPage = styled.div`
  background: #fff;

  .cn-intro-hero {
    text-align: center;
    padding: 48px 24px 36px;
    border-bottom: 1px solid ${BD};
  }

  .cn-intro-lockup {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 22px;
    margin-bottom: 18px;
  }

  .cn-intro-logo-cn {
    height: 44px;
    width: auto;
    display: block;
  }

  .cn-intro-logo-div {
    width: 1px;
    height: 34px;
    background: rgba(0, 0, 0, 0.15);
    display: block;
  }

  .cn-intro-logo-ti {
    height: 28px;
    width: auto;
    display: block;
  }

  .cn-intro-hero-title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 10px;
    color: ${T1};
  }

  .cn-intro-body {
    max-width: 720px;
    margin: 0 auto;
    padding: 36px 24px;
  }

  .cn-intro-steps {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 32px;
  }

  .cn-intro-step {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 20px 0;
    border-bottom: 1px solid ${BD};
  }

  .cn-intro-step:first-child {
    padding-top: 0;
  }

  .cn-intro-step:last-child {
    border-bottom: none;
  }

  .cn-intro-step-num {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${BLUE};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .cn-intro-step-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 4px;
    color: ${T1};
  }

  .cn-intro-step-desc {
    font-size: 14px;
    color: ${T2};
    line-height: 1.6;
  }

  .cn-intro-step-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: ${BLUE_LT};
    color: ${BLUE};
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 99px;
    margin-top: 6px;
  }

  .cn-intro-resources {
    background: ${GRAY_50};
    border-radius: 12px;
    padding: 20px 22px;
    margin-bottom: 28px;
  }

  .cn-intro-resources-title {
    font-size: 13px;
    font-weight: 600;
    color: ${T2};
    margin-bottom: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .cn-intro-resource-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: ${T2};
    padding: 6px 0;
  }

  .cn-intro-resource-row a {
    color: ${BLUE};
  }

  .cn-intro-resource-dash {
    font-size: 12px;
  }

  /* qualified with #root.cnTheme + !important to beat the global span color rule */
  #root.cnTheme & .cn-intro-resource-dash {
    color: ${T3} !important;
  }

  .cn-intro-cta-bar {
    background: ${GRAY_50};
    border-top: 1px solid ${BD};
    padding: 18px 24px;
  }

  .cn-intro-cta-inner {
    max-width: 720px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .cn-intro-cta-text-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 2px;
    color: ${T1};
  }

  .cn-intro-cta-text-sub {
    font-size: 13px;
    color: ${T2};
  }

  .cn-intro-cta-btn {
    font-size: 14px;
    font-weight: 600;
    padding: 12px 28px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-family: inherit;
    background: ${BLUE};
    color: #fff;
    flex-shrink: 0;

    &:hover {
      background: ${BLUE_DK};
    }
  }

  .cn-intro-terms {
    font-size: 12px;
    color: ${T3};
    text-align: center;
    max-width: 720px;
    margin: 10px auto 0;
  }
`;

interface CNIntroScreenProps {
  onContinue: () => unknown;
}

const CNIntroScreen: React.FC<CNIntroScreenProps> = ({ onContinue }) => {
  return (
    <IntroPage className="cn-intro-page">
      <div className="cn-intro-hero">
        <div className="cn-intro-lockup">
          <img
            className="cn-intro-logo-cn"
            src="/images/cn-logo.png"
            alt="Charity Navigator"
          />
          <span className="cn-intro-logo-div" />
          <img
            className="cn-intro-logo-ti"
            src="/images/logo_primary.png"
            alt="True Impact"
          />
        </div>
        <div className="cn-intro-hero-title">
          Your path to a Charity Navigator Impact rating
        </div>
      </div>

      <div className="cn-intro-body">
        <div className="cn-intro-steps">
          <div className="cn-intro-step">
            <div className="cn-intro-step-num">1</div>
            <div className="cn-intro-step-body">
              <div className="cn-intro-step-title">
                Set up your program profiles
              </div>
              <div className="cn-intro-step-desc">
                Create a profile for each program you run &mdash; cause area,
                activities, and impact model. Profiles are your account setup
                and are reused across all reports. This step does not count
                toward a Charity Navigator Impact rating on its own.
              </div>
            </div>
          </div>
          <div className="cn-intro-step">
            <div className="cn-intro-step-num">2</div>
            <div className="cn-intro-step-body">
              <div className="cn-intro-step-title">
                Create a full impact report
              </div>
              <div className="cn-intro-step-desc">
                Once a profile is complete, create a full report for that
                program &mdash; one per program, per year. Submitting it
                finalizes that program&rsquo;s report for the year. Charity
                Navigator selects which finalized report to rate, typically
                your largest program.
              </div>
              <div className="cn-intro-step-badge">This is what gets rated</div>
            </div>
          </div>
        </div>

        <div className="cn-intro-resources">
          <div className="cn-intro-resources-title">Before you begin</div>
          <div className="cn-intro-resource-row">
            <span className="cn-intro-resource-dash">&mdash;</span>{' '}
            <a
              href="https://support.trueimpact.com/getting-started-with-the-impact-reporting-network"
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch: Getting started with the Impact Reporting Network
            </a>
          </div>
          <div className="cn-intro-resource-row">
            <span className="cn-intro-resource-dash">&mdash;</span>{' '}
            <a
              href="https://docs.google.com/document/d/1HoEZXjPD5oXyVBf0ZbG-YYJYUi-7eOmKdWL-15jUZfE/edit"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download the Report Builder preparation worksheet
            </a>
          </div>
          <div className="cn-intro-resource-row">
            <span className="cn-intro-resource-dash">&mdash;</span>{' '}
            <a
              href="https://support.trueimpact.com/nonprofit-impact-reporting-resources"
              target="_blank"
              rel="noopener noreferrer"
            >
              Help Center &mdash; reporting resources &amp; FAQs
            </a>
          </div>
        </div>
      </div>

      <div className="cn-intro-cta-bar">
        <div className="cn-intro-cta-inner">
          <div>
            <div className="cn-intro-cta-text-title">Ready to get started?</div>
            <div className="cn-intro-cta-text-sub">
              You can save progress and return at any time.
            </div>
          </div>
          <button
            type="button"
            className="cn-intro-cta-btn"
            onClick={onContinue}
          >
            Enter True Impact &rarr;
          </button>
        </div>
        <div className="cn-intro-terms">
          By clicking &ldquo;Enter True Impact&rdquo; you agree to True
          Impact&rsquo;s Terms of Use.
        </div>
      </div>
    </IntroPage>
  );
};

export default CNIntroScreen;
